import React from 'react'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { TOUCH_MIN } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { useRegisterPageTabs } from 'contexts/PageTabsContext'
import type { PageTabOption } from 'contexts/PageTabsContext'
import { MobilePageTabBar, tabLabel } from 'components/MobilePageTabBar'

export type MobileTabOption<T extends string | number = string | number> = PageTabOption<T>

interface MobileTabSelectProps<T extends string | number> {
  tabs: MobileTabOption<T>[]
  value: T
  onChange: (value: T) => void
  ariaLabel?: string
  label?: string
  variant?: 'standard' | 'scrollable' | 'fullWidth'
  desktop?: React.ReactNode
}

export function MobileTabSelect<T extends string | number>({
  tabs,
  value,
  onChange,
  ariaLabel = 'Page sections',
  label = 'Section',
  variant = 'scrollable',
  desktop,
}: MobileTabSelectProps<T>) {
  const isMobile = useIsMobile()

  // Always publish to the layout when a provider exists. The bottom bar is
  // CSS-hidden on desktop; gating on isMobile left in-page tabs on iOS PWAs
  // that lie about viewport width.
  const registered = useRegisterPageTabs(true, {
    tabs: tabs as PageTabOption[],
    value,
    onChange: onChange as (value: string | number) => void,
    ariaLabel,
    label,
  })

  // Phone: never keep an in-page MUI strip. The layout bottom bar owns tabs
  // when a provider is present; otherwise render the same bar inline.
  if (isMobile) {
    if (registered) {
      return null
    }
    return (
      <MobilePageTabBar
        tabs={tabs as PageTabOption[]}
        value={value}
        onChange={onChange as (value: string | number) => void}
        ariaLabel={ariaLabel}
        label={label}
      />
    )
  }

  if (desktop) {
    return <>{desktop}</>
  }

  return (
    <Tabs
      value={value}
      onChange={(_, next) => onChange(next as T)}
      aria-label={ariaLabel}
      variant={variant}
      scrollButtons={variant === 'scrollable' ? 'auto' : false}
      allowScrollButtonsMobile
      textColor='primary'
      indicatorColor='primary'
    >
      {tabs.map(tab => (
        <Tab
          key={String(tab.value)}
          value={tab.value}
          label={tabLabel(tab)}
          disabled={tab.disabled}
          sx={{ textTransform: 'none', minHeight: TOUCH_MIN }}
        />
      ))}
    </Tabs>
  )
}
